import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import FormHeader from "@/components/FormHeader"
import Modal from "@/components/Modal"
import SkeletonRow from "@/components/SkeletonRow"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useDebounce } from "@/hooks/useDebounce"
import api from "@/lib/axios"
import { getListPeriode } from "@/services/getData"
import { PaginationData, Periode, DataNilaiElearning } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import toast from "react-hot-toast"
import { MdSync } from "react-icons/md"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { Pagination } from "./Pagination"

interface DataTableProps<TData extends DataNilaiElearning, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  paginationData: PaginationData<DataNilaiElearning[]>
  isPending: boolean
}

const postDataSyncNilaiElearning = async ({
  id_sys_periode,
}: {
  id_sys_periode: string
}) => {
  const response = await api.post(
    "/operator-pasca/user-elearning/sync-nilai-elearning",
    {
      id_sys_periode,
    }
  )
  return response.data
}

const postDataStoreNilaiElearning = async ({
  id_sys_periode,
}: {
  id_sys_periode: string
}) => {
  const response = await api.post(
    "/operator-pasca/user-elearning/store-nilai-elearning",
    {
      id_sys_periode,
    }
  )
  return response.data
}

export function DataTable<TData extends DataNilaiElearning, TValue>({
  columns,
  data,
  paginationData,
  isPending: isPendingDataPendaftaran,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [searchParams] = useSearchParams()

  const [form, setForm] = useState({
    id_sys_periode: "",
    tahun: `${new Date().getFullYear()}` || "",
  })

  const [showSync, setShowSync] = useState(false)
  const [showStoreNilai, setShowStoreNilai] = useState(false)
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  )
  const debouncedQuery = useDebounce(searchQuery, 1000)
  const navigate = useNavigate()
  const location = useLocation()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  })
  const listPeriodePasca = dataPeriode
    ? dataPeriode?.filter((periode: Periode) => {
        return periode.jalur_jenis?.nama_jalur_jenis_slug === "pascasarjana"
      })
    : []

  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    mutationFn: postDataSyncNilaiElearning,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNilaiElearning"] })
      setShowSync(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const {
    mutate: mutateStoreNilaiElearning,
    isPending: isPendingStoreNilaiElearning,
  } = useMutation({
    mutationFn: postDataStoreNilaiElearning,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNilaiElearning"] })
      setShowStoreNilai(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSyncNilaiElearning = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutate({
      id_sys_periode: form.id_sys_periode,
    })
  }

  const handleStoreNilaiElearning = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()

    mutateStoreNilaiElearning({
      id_sys_periode: form.id_sys_periode,
    })
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page")
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery)
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search")
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search])

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by username atau nama..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi Nilai Elearning
            <MdSync />
          </Button>
          <Button
            variant={"outline"}
            className="flex items-center gap-x-3"
            onClick={() => setShowStoreNilai(true)}
          >
            Store Nilai TPA & TKB
            <MdSync />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isPendingDataPendaftaran ? (
        <div className="flex flex-col gap-y-3">
          {Array(10)
            .fill(null)
            .map((_, index) => {
              return <SkeletonRow key={index} />
            })}
        </div>
      ) : (
        <div className="border rounded-xl">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => {
                return (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <>
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        </>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <>
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      </>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 2}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={!isPending}
      >
        <form
          onSubmit={handleSyncNilaiElearning}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Nilai Elearning"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Select
              required
              value={form.id_sys_periode}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Periode"} />
              </SelectTrigger>
              <SelectContent>
                {listPeriodePasca?.map((periode: Periode) => (
                  <SelectItem
                    key={periode.id_sys_periode}
                    value={`${periode.id_sys_periode}`}
                  >
                    {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                    Gelombang {periode.gelombang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showStoreNilai}
        onClose={() => setShowStoreNilai(false)}
        closeable={isPendingStoreNilaiElearning}
      >
        <form
          onSubmit={handleStoreNilaiElearning}
          className="flex flex-col gap-y-3"
        >
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Nilai TPA & TKB"
            description={`Apakah anda yakin akan melakukan sinkronisasi data?`}
          />
          <div>
            <Label>Periode</Label>
            <Select
              required
              value={form.id_sys_periode}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Periode"} />
              </SelectTrigger>
              <SelectContent>
                {listPeriodePasca?.map((periode: Periode) => (
                  <SelectItem
                    key={periode.id_sys_periode}
                    value={`${periode.id_sys_periode}`}
                  >
                    {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                    Gelombang {periode.gelombang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPendingStoreNilaiElearning}
              onClick={() => setShowStoreNilai(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPendingStoreNilaiElearning}
            >
              {isPendingStoreNilaiElearning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
